/**
 * The questionnaire — the fields and the JSON, as one document in two views.
 *
 * Same arrangement as the opt-in texts block, deliberately: editing a field rewrites the JSON, and
 * pasting a valid document repopulates the fields. Invalid JSON is left exactly as typed with the
 * parser's message in the status, because reverting someone's text mid-edit is worse than showing
 * them the error.
 *
 * Field labels are the JSON keys, in mono. That is what makes the two modes line up one to one in
 * the head of whoever is using them.
 *
 * The document carries the title, the description and the questions in the three languages.
 * Identification and targeting — BU, slug, window, trigger, layout, eligibility — stay in the
 * blocks above: a slug or an opt-in condition arriving from a pasted document is the easiest way
 * to aim a survey at the wrong people.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTButton, UTTextArea, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useState } from 'react';
import type { DragEvent, JSX } from 'react';
import { QuestionEditor } from './QuestionEditor';
import type { UseSurveyBuilderForm } from '../../../Hooks/useSurveyBuilderForm';
import type { SurveyLayout } from '../../../Types/Surveys.types';
import {
  SURVEY_LANGUAGES,
  SURVEY_LANGUAGE_LABELS,
  SURVEY_LIMITS,
} from '../../../Types/Surveys.types';
import type { QuestionnaireProblem } from '../../../Utils/questionnaireJson';
import {
  createQuestionnairePrompt,
  createQuestionnaireTemplate,
} from '../../../Utils/questionnaireJson';
import {
  countTranslatedLabels,
  isQuestionFrozen,
  questionsOfStep,
  stepSummary,
  stepsOf,
} from '../../../Utils/surveyForm';
import { LAYOUT_LABELS } from '../../../Utils/surveyFormat';
import { useActionToast } from '@/Hooks/useActionToast';
import { CollapsibleSection } from '@/Shared/components/CollapsibleSection/CollapsibleSection';
import { SchemaDialog } from '@/Shared/components/SchemaDialog/SchemaDialog';
import { Tag } from '@/Shared/components/Tag/Tag';

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
`;

const titleRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-base);
  flex-wrap: wrap;
`;

const titleBlockStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  min-width: 0;
`;

const titleStyle = css`
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
`;

const subtitleStyle = css`
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const spacerStyle = css`
  flex: 1;
`;

const controlsStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  flex-wrap: wrap;
  padding-bottom: var(--space-s);
  border-bottom: 1px solid var(--color-divider);
`;

const dividerStyle = css`
  width: 1px;
  height: 22px;
  background: var(--color-divider);
`;

const miniLabelStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const statusStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-2xs);
  max-width: 360px;
`;

const jsonModeStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
`;

const jsonActionsStyle = css`
  display: flex;
  justify-content: flex-end;
`;

const jsonAreaStyle = css`
  width: 100%;
  height: 560px;
  padding: var(--space-m);
  border-radius: var(--radius-m);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface-sunken);
  color: var(--color-text);
  font-family: var(--font-mono);
  font-size: 12px;
  line-height: 1.6;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

/** What is still missing, listed rather than hidden behind a disabled button. */
const pendingStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-warning);
  border-radius: var(--radius-m);
  background: var(--color-warning-subtle);
`;

const pendingTitleStyle = css`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--gl-yellow-dark);
`;

/** A pendência that can be gone to, not only read. */
const pendingLinkStyle = css`
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: var(--color-text);
  }
`;

const pendingListStyle = css`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 220px;
  overflow: auto;
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding-top: var(--space-m);
  border-top: 1px solid var(--color-divider);
`;

const sectionHeadStyle = css`
  display: flex;
  align-items: baseline;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const sectionIndexStyle = css`
  font-size: var(--font-size-md);
  color: var(--color-primary);
`;

const sectionTitleStyle = css`
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
`;

const sectionHintStyle = css`
  margin: 0;
  flex: 1 1 260px;
  min-width: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const fieldsGridStyle = css`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: var(--space-s) var(--space-m);
`;

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const monoLabelStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const fieldNameStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const errorStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-danger);
`;

const stepStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-s);
  padding: var(--space-m) var(--space-ml) var(--space-ml);
`;

const stepHintStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const stepTailStyle = css`
  min-height: 12px;
  border-radius: var(--radius-m);
`;

const stepTailActiveStyle = css`
  border: 1px dashed var(--color-primary);
  background: var(--color-primary-subtle);
  min-height: 32px;
`;

const emptyStepStyle = css`
  margin: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const layoutFieldStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-xs);
`;

const layoutLabelStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  white-space: nowrap;
`;

const layoutSelectStyle = css`
  height: 32px;
  padding: 0 var(--space-xs);
  border-radius: var(--radius-control);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: inherit;
  font-size: var(--font-size-xs);
`;

const stepsFooterStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

export interface SurveyQuestionsBlockProps {
  builder: UseSurveyBuilderForm;
  onPreview: () => void;
}

export const SurveyQuestionsBlock = ({
  builder,
  onPreview,
}: SurveyQuestionsBlockProps): JSX.Element => {
  const toast = useActionToast();
  const [mode, setMode] = useState<'fields' | 'json'>('fields');
  const [isSchemaOpen, setIsSchemaOpen] = useState(false);
  const [draggingUid, setDraggingUid] = useState<string | null>(null);
  const [dropTargetUid, setDropTargetUid] = useState<string | null>(null);
  const [dropTargetStep, setDropTargetStep] = useState<number | null>(null);

  /**
   * Which steps are collapsed — the exception, not the rule.
   *
   * Everything opens by default: whoever came in to edit wants to see what is there. Tracking the
   * closed ones rather than the open ones means a step created later is open without anyone having
   * to remember to add it.
   */
  const [closedSteps, setClosedSteps] = useState<number[]>([]);

  const isStepOpen = (step: number): boolean => !closedSteps.includes(step);

  const openStep = (step: number): void => {
    setClosedSteps(current => current.filter(entry => entry !== step));
  };

  const toggleStep = (step: number): void => {
    setClosedSteps(current =>
      current.includes(step) ? current.filter(entry => entry !== step) : [...current, step],
    );
  };

  const { form, language, problems, errors } = builder;
  const steps = stepsOf(form.questions);
  const texts = form.texts[language];
  const allClosed = steps.every(step => closedSteps.includes(step));

  /**
   * Takes the reader to the pendência.
   *
   * Opens the step it lives in, switches to the language the missing text belongs to, then focuses
   * the field. The focus waits a frame: the step has just been revealed and the input is not
   * focusable while its container is still `display: none`.
   */
  const goToProblem = (problem: QuestionnaireProblem): void => {
    if (problem.step !== null) openStep(problem.step);
    if (problem.language && problem.language !== language) builder.setLanguage(problem.language);

    if (!problem.fieldId) return;

    window.requestAnimationFrame(() => {
      const field = document.getElementById(problem.fieldId as string);

      field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (field as HTMLElement | null)?.focus?.();
    });
  };

  /**
   * Hands over a document to paste elsewhere: the empty template, or what is built right now,
   * which is how one questionnaire seeds the next.
   */
  const copy = async (text: string, what: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${what} copiado`);
    } catch (error) {
      toast.failure(`Não foi possível copiar ${what.toLowerCase()}`, error);
    }
  };

  const endDrag = (): void => {
    setDraggingUid(null);
    setDropTargetUid(null);
    setDropTargetStep(null);
  };

  const dropOnQuestion = (targetUid: string): void => {
    if (!draggingUid || draggingUid === targetUid) {
      endDrag();

      return;
    }

    const target = form.questions.find(question => question.uid === targetUid);
    if (!target) {
      endDrag();

      return;
    }

    const inStep = questionsOfStep(form.questions, target.step).filter(
      question => question.uid !== draggingUid,
    );
    const index = inStep.findIndex(question => question.uid === targetUid);

    builder.moveQuestion(draggingUid, target.step, index === -1 ? inStep.length : index);
    endDrag();
  };

  const dropOnStepTail = (step: number): void => {
    if (!draggingUid) {
      endDrag();

      return;
    }

    const inStep = questionsOfStep(form.questions, step).filter(
      question => question.uid !== draggingUid,
    );

    builder.moveQuestion(draggingUid, step, inStep.length);
    endDrag();
  };

  const allowDrop = (event: DragEvent<HTMLElement>): void => {
    event.preventDefault();
  };

  return (
    <div className={wrapStyle}>
      <div className={titleRowStyle}>
        <div className={titleBlockStyle}>
          <h2 className={titleStyle}>Questionário</h2>
          <p className={subtitleStyle}>
            Monte pelos campos ou cole o JSON pronto. Os dois lados ficam sempre iguais.
          </p>
        </div>
        <div className={spacerStyle} />
        <UTButton
          fitContent
          variant='tertiary'
          size='small'
          id='survey-builder-schema'
          text='Ver schema'
          icon='Misc.Lightbulb'
          onClick={() => {
            setIsSchemaOpen(true);
          }}
        />
        <UTButton
          fitContent
          variant='tertiary'
          size='small'
          id='survey-builder-copy-template'
          text='Copiar template'
          icon='Communication.CopySimple'
          onClick={() => {
            void copy(createQuestionnaireTemplate(), 'Template');
          }}
        />
        <UTButton
          fitContent
          variant='secondary'
          size='small'
          id='survey-builder-preview'
          text='Pré-visualizar'
          icon='Misc.Eye'
          onClick={onPreview}
        />
      </div>

      <div className={controlsStyle}>
        <Tag
          tone='primary'
          selected={mode === 'fields'}
          onClick={() => {
            setMode('fields');
          }}
        >
          Campos
        </Tag>
        <Tag
          tone='primary'
          selected={mode === 'json'}
          onClick={() => {
            setMode('json');
          }}
        >
          JSON
        </Tag>

        <span className={dividerStyle} />

        <span className={miniLabelStyle}>Idioma</span>
        {SURVEY_LANGUAGES.map(entry => {
          const { filled, total } = countTranslatedLabels(form, entry);

          return (
            <Tag
              key={entry}
              id={`survey-builder-lang-tab-${entry}`}
              tone='primary'
              selected={language === entry}
              onClick={() => {
                builder.setLanguage(entry);
              }}
            >
              {`${SURVEY_LANGUAGE_LABELS[entry]} ${filled}/${total}`}
            </Tag>
          );
        })}

        <div className={spacerStyle} />

        {builder.jsonError ? (
          <span
            id='survey-builder-json-status'
            className={statusStyle}
            style={{ color: 'var(--color-danger)' }}
          >
            <GLIcon variant='Notifications.WarningCircle' size={14} fill='var(--color-danger)' />
            JSON inválido: {builder.jsonError}
          </span>
        ) : (
          <span
            id='survey-builder-json-status'
            className={statusStyle}
            style={{ color: 'var(--color-success)' }}
          >
            <GLIcon variant='Communication.CheckCircle' size={14} fill='var(--color-success)' />
            JSON válido — {form.questions.length}{' '}
            {form.questions.length === 1 ? 'pergunta' : 'perguntas'}, {steps.length}{' '}
            {steps.length === 1 ? 'passo' : 'passos'}, 3 idiomas
          </span>
        )}
      </div>

      {problems.length > 0 ? (
        <div id='survey-builder-problems' className={pendingStyle}>
          <span className={pendingTitleStyle}>
            <GLIcon variant='Notifications.WarningCircle' size={14} fill='var(--gl-yellow-dark)' />
            Falta preencher antes de continuar — {problems.length}{' '}
            {problems.length === 1 ? 'pendência' : 'pendências'} nos três idiomas
          </span>
          <ul className={pendingListStyle}>
            {problems.map(problem => (
              <li key={problem.id}>
                {problem.fieldId ? (
                  <button
                    type='button'
                    className={pendingLinkStyle}
                    onClick={() => {
                      goToProblem(problem);
                    }}
                  >
                    {problem.message}
                  </button>
                ) : (
                  problem.message
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {mode === 'json' ? (
        <div className={jsonModeStyle}>
          <div className={jsonActionsStyle}>
            <UTButton
              fitContent
              variant='tertiary'
              size='small'
              id='survey-builder-json-copy-current'
              text='Copiar este JSON'
              icon='Communication.CopySimple'
              onClick={() => {
                void copy(builder.json, 'JSON do questionário');
              }}
            />
          </div>
          <textarea
            id='survey-builder-json-input'
            className={jsonAreaStyle}
            spellCheck={false}
            value={builder.json}
            aria-label='JSON do questionário'
            onChange={event => {
              builder.setJson(event.target.value);
            }}
          />
        </div>
      ) : (
        <>
          <section id='survey-builder-section-texts' className={sectionStyle}>
            <div className={sectionHeadStyle}>
              <span className={sectionIndexStyle}>①</span>
              <span className={sectionTitleStyle}>Abertura</span>
              <p className={sectionHintStyle}>
                O que a pessoa lê antes da primeira pergunta — o cabeçalho do modal.
              </p>
            </div>

            <div className={fieldsGridStyle}>
              <div className={fieldStyle} style={{ gridColumn: '1 / span 7' }}>
                <span className={monoLabelStyle}>
                  title <span className={fieldNameStyle}>· título</span>
                </span>
                <UTTextField
                  id='survey-builder-title'
                  value={texts.title}
                  state={errors.title && language === 'en' ? 'error' : 'normal'}
                  onChange={event => {
                    builder.setSurveyText('title', event.target.value);
                  }}
                />
                {errors.title && language === 'en' ? (
                  <span className={errorStyle}>{errors.title}</span>
                ) : null}
              </div>

              <div className={fieldStyle} style={{ gridColumn: '1 / -1', maxWidth: '68ch' }}>
                <span className={monoLabelStyle}>
                  description <span className={fieldNameStyle}>· subtítulo (opcional)</span>
                </span>
                <UTTextArea
                  id='survey-builder-description'
                  textAreaSize='small'
                  value={texts.description}
                  onChange={event => {
                    builder.setSurveyText('description', event.target.value);
                  }}
                />
              </div>
            </div>
          </section>

          <section id='survey-builder-section-questions' className={sectionStyle}>
            <div className={sectionHeadStyle}>
              <span className={sectionIndexStyle}>②</span>
              <span className={sectionTitleStyle}>Perguntas</span>
              <p className={sectionHintStyle}>
                Arraste pela alça para reordenar ou mover entre passos — as setas fazem o mesmo sem
                mouse, e arrastar sobre um passo recolhido abre ele.
              </p>
              <div className={spacerStyle} />

              {/* `layout` lives here, not in "Entrega": ele decide se um passo é uma tela ou só um
                  bloco na mesma página, que é uma decisão sobre o questionário — e só faz sentido
                  lida ao lado dos passos que ela governa. */}
              <div className={layoutFieldStyle}>
                <span className={layoutLabelStyle}>Passos viram</span>
                <select
                  id='survey-builder-layout'
                  className={layoutSelectStyle}
                  value={form.layout}
                  aria-label='Layout do questionário'
                  onChange={event => {
                    builder.setField('layout', event.target.value as SurveyLayout);
                  }}
                >
                  {(['ONE_PER_STEP', 'SINGLE_PAGE'] as SurveyLayout[]).map(layout => (
                    <option key={layout} value={layout}>
                      {LAYOUT_LABELS[layout]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {steps.map(step => {
              const inStep = questionsOfStep(form.questions, step);

              const stepProblemCount = problems.filter(problem => problem.step === step).length;

              return (
                /* Dragging over a collapsed step opens it: collapsing the other steps to tidy up
                   must not take away the only way to move a question with a mouse. */
                <div
                  key={step}
                  onDragOver={() => {
                    if (draggingUid && !isStepOpen(step)) openStep(step);
                  }}
                >
                  <CollapsibleSection
                    id={`survey-builder-step-${step}`}
                    title={`Passo ${step}`}
                    icon='Menu.Rows'
                    count={stepSummary(inStep, language)}
                    summary={
                      stepProblemCount > 0
                        ? `${stepProblemCount} ${stepProblemCount === 1 ? 'pendência' : 'pendências'}`
                        : undefined
                    }
                    open={isStepOpen(step)}
                    keepMounted
                    onToggle={() => {
                      toggleStep(step);
                    }}
                  >
                    <div className={stepStyle}>
                      {inStep.length === 0 ? (
                        <p className={emptyStepStyle}>
                          Passo vazio. Arraste uma pergunta para cá ou crie uma.
                        </p>
                      ) : null}

                      {inStep.map((question, index) => (
                        <QuestionEditor
                          key={question.uid}
                          question={question}
                          index={index}
                          total={inStep.length}
                          step={step}
                          language={language}
                          frozen={isQuestionFrozen(question, builder.responseCount)}
                          isDragging={draggingUid === question.uid}
                          isDropTarget={dropTargetUid === question.uid}
                          onDragStart={setDraggingUid}
                          onDragEnd={endDrag}
                          onDragOver={setDropTargetUid}
                          onDrop={dropOnQuestion}
                          onMove={direction => {
                            builder.moveQuestion(question.uid, step, index + direction);
                          }}
                          onRemove={() => {
                            builder.removeQuestion(question.uid);
                          }}
                          onDuplicate={() => {
                            builder.duplicateQuestion(question.uid);
                          }}
                          onTypeChange={type => {
                            builder.setQuestionType(question.uid, type);
                          }}
                          onKeyChange={value => {
                            builder.setQuestionKey(question.uid, value);
                          }}
                          onLabelChange={value => {
                            builder.setQuestionLabel(question.uid, value);
                          }}
                          onHelpTextChange={value => {
                            builder.setQuestionHelpText(question.uid, value);
                          }}
                          onRequiredChange={value => {
                            builder.setQuestionRequired(question.uid, value);
                          }}
                          onNumberChange={(field, value) => {
                            builder.setQuestionNumber(question.uid, field, value);
                          }}
                          onConfigTextChange={(field, value) => {
                            builder.setQuestionConfigText(question.uid, field, value);
                          }}
                          onOptionValueChange={(optionUid, value) => {
                            builder.setOptionValue(question.uid, optionUid, value);
                          }}
                          onOptionLabelChange={(optionUid, value) => {
                            builder.setOptionLabel(question.uid, optionUid, value);
                          }}
                          onOptionFlagChange={(optionUid, flag, value) => {
                            builder.setOptionFlag(question.uid, optionUid, flag, value);
                          }}
                          onOptionMove={(optionUid, direction) => {
                            builder.moveOption(question.uid, optionUid, direction);
                          }}
                          onOptionAdd={() => {
                            builder.addOption(question.uid);
                          }}
                          onOptionRemove={optionUid => {
                            builder.removeOption(question.uid, optionUid);
                          }}
                          onRowKeyChange={(rowUid, value) => {
                            builder.setRowKey(question.uid, rowUid, value);
                          }}
                          onRowLabelChange={(rowUid, value) => {
                            builder.setRowLabel(question.uid, rowUid, value);
                          }}
                          onRowTypeChange={(rowUid, type) => {
                            builder.setRowType(question.uid, rowUid, type);
                          }}
                          onRowRequiredChange={(rowUid, value) => {
                            builder.setRowRequired(question.uid, rowUid, value);
                          }}
                          onRowAdd={() => {
                            builder.addRow(question.uid);
                          }}
                          onRowRemove={rowUid => {
                            builder.removeRow(question.uid, rowUid);
                          }}
                        />
                      ))}

                      {/* The tail is what makes "move to the end of this step" — and "move into an
                      empty step" — expressible with a drag. */}
                      <div
                        className={`${stepTailStyle} ${dropTargetStep === step ? stepTailActiveStyle : ''}`}
                        onDragOver={event => {
                          allowDrop(event);
                          setDropTargetStep(step);
                        }}
                        onDragLeave={() => {
                          setDropTargetStep(current => (current === step ? null : current));
                        }}
                        onDrop={event => {
                          event.preventDefault();
                          dropOnStepTail(step);
                        }}
                      />

                      <UTButton
                        fitContent
                        variant='secondary'
                        size='small'
                        id={`survey-builder-add-question-step-${step}`}
                        text='Nova pergunta'
                        icon='Arrows.Plus'
                        disabled={form.questions.length >= SURVEY_LIMITS.questions}
                        onClick={() => {
                          builder.addQuestion(step);
                        }}
                      />
                    </div>
                  </CollapsibleSection>
                </div>
              );
            })}

            <div className={stepsFooterStyle}>
              <UTButton
                fitContent
                variant='secondary'
                size='small'
                id='survey-builder-add-step'
                text='Novo passo'
                icon='Arrows.Plus'
                disabled={steps.length >= SURVEY_LIMITS.steps}
                onClick={builder.addStep}
              />
              <UTButton
                fitContent
                variant='tertiary'
                size='small'
                id='survey-builder-toggle-all-steps'
                text={allClosed ? 'Expandir todos' : 'Recolher todos'}
                icon={allClosed ? 'Arrows.CaretDown' : 'Arrows.CaretUp'}
                onClick={() => {
                  setClosedSteps(allClosed ? [] : steps);
                }}
              />
              <span className={stepHintStyle}>
                {steps.length} de {SURVEY_LIMITS.steps} passos · {form.questions.length} de{' '}
                {SURVEY_LIMITS.questions} perguntas
              </span>
            </div>
          </section>
        </>
      )}

      {isSchemaOpen ? (
        <SchemaDialog
          id='survey-builder-schema-modal'
          title='Schema do questionário'
          description='O JSON cobre só o conteúdo — BU, slug, janela, gatilho e elegibilidade ficam nos blocos acima.'
          schema={createQuestionnaireTemplate()}
          prompt={createQuestionnairePrompt()}
          promptHint='Cole numa IA junto com a descrição do que você quer descobrir e traga o questionário pronto, nos três idiomas.'
          onClose={() => {
            setIsSchemaOpen(false);
          }}
        >
          Campos ausentes viram vazios. `key` e `value` são códigos estáveis, iguais nos três
          idiomas: o prompt diz isso à IA, porque traduzir um identificador é o erro que parte a
          contagem em duas sem avisar ninguém.
        </SchemaDialog>
      ) : null}
    </div>
  );
};
